import { useEffect, useMemo, useRef, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Clock, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    formatDay, formatTime, offsetLabel, parseClock, zonedDayStart, zonedParts, zonedToInstant
} from "@/lib/timezone";
import { fetchAvailability, type Availability, type BusyBlock } from "./availabilityApi";

/**
 * Outlook / Teams scheduling assistant: one day's timeline with a row per
 * person (the organiser first), their busy blocks, their working hours
 * (everything outside is shaded), the proposed meeting as a band across every
 * row, and "Suggested times" when everyone is free. Times are the organiser's
 * zone; hovering a person shows the proposed time in their own zone.
 *
 * Free/busy only — titles appear solely where the server says the viewer may
 * see them. (This file is kept identical in Mail web and Connect web.)
 */

export interface AssistantPerson {
    email: string;
    displayName: string | null;
}

interface Props {
    token: string | undefined;
    organizer: AssistantPerson;
    attendees: AssistantPerson[];
    /** Proposed start (an instant). */
    start: Date;
    durationMin: number;
    /** The organiser's zone and working hours — the grid is drawn in it. */
    timeZone: string;
    workDays: number[];
    workStart: string;
    workEnd: string;
    onPick: (start: Date) => void;
}

/** Width of one half-hour on the timeline. */
const CELL = 26;
const NAME_W = 168;

interface Band { from: number; to: number }

export function SchedulingAssistant({
    token, organizer, attendees, start, durationMin, timeZone, workDays, workStart, workEnd, onPick
}: Props) {
    const dayStart = useMemo(() => zonedDayStart(start, timeZone), [start, timeZone]);
    const dayEnd = useMemo(() => zonedDayStart(start, timeZone, 1), [start, timeZone]);
    const totalMin = (dayEnd.getTime() - dayStart.getTime()) / 60_000;
    const width = (totalMin / 30) * CELL;
    const x = (t: number) => (Math.max(0, Math.min(totalMin, (t - dayStart.getTime()) / 60_000)) / 30) * CELL;

    const people = useMemo(() => {
        const seen = new Set<string>();
        const list: Array<AssistantPerson & { organizer: boolean }> = [];
        for (const p of [{ ...organizer, organizer: true }, ...attendees.map((a) => ({ ...a, organizer: false }))]) {
            const key = p.email.trim().toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            list.push({ ...p, email: key });
        }
        return list;
    }, [organizer, attendees]);

    const emails = people.map((p) => p.email);
    const availability = useQuery({
        queryKey: ["availability", emails.join(","), dayStart.toISOString(), token],
        queryFn: () => fetchAvailability(emails, dayStart, dayEnd, token),
        enabled: Boolean(token) && emails.length > 0,
        staleTime: 60_000,
        retry: 1
    });

    const selStart = start.getTime();
    const selEnd = selStart + durationMin * 60_000;

    /** Working hours of one person, as instants inside this day. */
    const bandsFor = (a: Availability | undefined, isOrganizer: boolean): Band[] | null => {
        const tz = isOrganizer ? timeZone : a?.timeZone;
        const days = isOrganizer ? workDays : a?.workDays;
        const ws = parseClock(isOrganizer ? workStart : a?.workStart);
        const we = parseClock(isOrganizer ? workEnd : a?.workEnd);
        if (!tz || !days || ws === null || we === null) return null;
        const bands: Band[] = [];
        const anchor = zonedParts(dayStart, tz);
        for (const offset of [-1, 0, 1]) {
            const d = new Date(Date.UTC(anchor.year, anchor.month - 1, anchor.day + offset));
            const iso = ((d.getUTCDay() + 6) % 7) + 1;
            if (!days.includes(iso)) continue;
            const y = d.getUTCFullYear(), m = d.getUTCMonth() + 1, day = d.getUTCDate();
            const from = zonedToInstant(y, m, day, Math.floor(ws / 60), ws % 60, tz).getTime();
            const to = zonedToInstant(y, m, day, Math.floor(we / 60), we % 60, tz).getTime();
            const f = Math.max(from, dayStart.getTime());
            const t = Math.min(to, dayEnd.getTime());
            if (t > f) bands.push({ from: f, to: t });
        }
        return bands;
    };

    const rows = people.map((p) => {
        const a = availability.data?.get(p.email);
        const known = p.organizer || Boolean(a?.available);
        const bands = known ? bandsFor(a, p.organizer) : null;
        const busy = (a?.busy ?? []).filter((b) => b.end.getTime() > dayStart.getTime() && b.start.getTime() < dayEnd.getTime());
        return { person: p, availability: a, known, bands, busy };
    });

    const conflictsAt = (s: number, e: number) => rows.filter((r) =>
        r.known && r.busy.some((b) => !b.allDay && b.start.getTime() < e && b.end.getTime() > s));

    const conflictNames = availability.data ? conflictsAt(selStart, selEnd).map((r) => label(r.person, r.availability)) : [];

    /** Up to four free (or least-busy) slots inside the organiser's working day. */
    const suggestions = useMemo(() => {
        if (!availability.data) return [];
        const orgBands = bandsFor(undefined, true);
        const bands = orgBands && orgBands.length > 0
            ? orgBands
            : [{ from: dayStart.getTime() + 9 * 3_600_000, to: dayStart.getTime() + 18 * 3_600_000 }];
        const now = Date.now();
        const dur = durationMin * 60_000;
        const scored: Array<{ at: number; score: number; conflicts: number }> = [];
        for (const band of bands) {
            for (let s = band.from; s + dur <= band.to; s += 30 * 60_000) {
                if (s < now) continue;
                let score = 0;
                let conflicts = 0;
                for (const r of rows) {
                    if (!r.known) continue;
                    const hit = r.busy.find((b) => !b.allDay && b.start.getTime() < s + dur && b.end.getTime() > s);
                    if (hit) { conflicts++; score += hit.showAs === "busy" ? 1 : 0.5; }
                    if (r.bands && !r.bands.some((w) => w.from <= s && w.to >= s + dur)) score += 0.3;
                }
                scored.push({ at: s, score, conflicts });
            }
        }
        const free = scored.filter((c) => c.conflicts === 0).sort((a, b) => a.score - b.score || a.at - b.at);
        if (free.length > 0) return free.slice(0, 4).sort((a, b) => a.at - b.at);
        return scored.sort((a, b) => a.score - b.score || a.at - b.at).slice(0, 3).sort((a, b) => a.at - b.at);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availability.data, dayStart.getTime(), durationMin, timeZone, workStart, workEnd, workDays.join(",")]);

    // Open scrolled to the start of the organiser's working day.
    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const ws = parseClock(workStart) ?? 9 * 60;
        const target = zonedToInstant(
            zonedParts(dayStart, timeZone).year, zonedParts(dayStart, timeZone).month, zonedParts(dayStart, timeZone).day,
            Math.floor(ws / 60), ws % 60, timeZone).getTime();
        const selX = x(selStart);
        const el = scrollRef.current;
        if (!el) return;
        const wanted = Math.min(x(target), selX) - CELL * 2;
        el.scrollLeft = Math.max(0, wanted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayStart.getTime(), timeZone, workStart]);

    const hours = Array.from({ length: Math.round(totalMin / 60) }, (_, h) => h);
    const shiftDay = (dir: 1 | -1) => onPick(new Date(selStart + dir * 86_400_000));

    return (
        <section aria-label="Scheduling assistant" className="space-y-2 rounded-lg border bg-card p-3">
            <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold">Scheduling assistant</h3>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => shiftDay(-1)} aria-label="Previous day"
                        className="flex h-7 w-7 items-center justify-center rounded-md border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                        <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                    <span className="min-w-[7rem] text-center text-xs font-medium">{formatDay(start, timeZone)}</span>
                    <button type="button" onClick={() => shiftDay(1)} aria-label="Next day"
                        className="flex h-7 w-7 items-center justify-center rounded-md border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                        <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                </div>
                <span className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" aria-hidden /> {timeZone} ({offsetLabel(timeZone, start)})
                </span>
            </div>

            <div ref={scrollRef} className="overflow-x-auto rounded-md border">
                <div className="relative" style={{ width: NAME_W + width }}>
                    {/* Time header: every half hour is a button that moves the meeting there. */}
                    <div className="sticky top-0 z-20 flex border-b bg-card">
                        <div className="sticky left-0 z-30 shrink-0 border-r bg-card" style={{ width: NAME_W }} />
                        <div className="relative flex" style={{ width }}>
                            {hours.map((h) => (
                                <div key={h} className="flex" style={{ width: CELL * 2 }}>
                                    {[0, 30].map((m) => {
                                        const at = dayStart.getTime() + (h * 60 + m) * 60_000;
                                        return (
                                            <button
                                                key={m}
                                                type="button"
                                                onClick={() => onPick(new Date(at))}
                                                aria-label={`Start at ${formatTime(new Date(at), timeZone)}`}
                                                className={cn(
                                                    "h-6 shrink-0 border-l text-left text-[10px] leading-6 text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                                                    m === 30 && "border-dashed border-l-border/50"
                                                )}
                                                style={{ width: CELL }}
                                            >
                                                {m === 0 ? <span className="pl-0.5">{formatTime(new Date(at), timeZone).replace(":00", "")}</span> : null}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>

                    {rows.map(({ person, availability: a, known, bands, busy }) => {
                        const tz = person.organizer ? timeZone : a?.timeZone ?? null;
                        const local = tz && tz !== timeZone
                            ? `${formatTime(start, tz)} their time · ${tz}`
                            : null;
                        return (
                            <div key={person.email} className="group flex border-b last:border-b-0">
                                <div
                                    className="sticky left-0 z-10 flex shrink-0 flex-col justify-center border-r bg-card px-2 py-1"
                                    style={{ width: NAME_W }}
                                    title={local ?? person.email}
                                >
                                    <span className="truncate text-xs font-medium">
                                        {person.organizer ? `${label(person, a)} (you)` : label(person, a)}
                                    </span>
                                    <span className="truncate text-[10px] text-muted-foreground">
                                        {!known && availability.data
                                            ? "No free/busy info"
                                            : local
                                                ? <><span className="group-hover:hidden">{tz}</span><span className="hidden group-hover:inline">{local}</span></>
                                                : person.organizer ? "Organiser" : "Required"}
                                    </span>
                                </div>
                                <div className={cn("relative h-10", bands ? "bg-muted/70" : "bg-muted/30")} style={{ width }}>
                                    {bands?.map((b) => (
                                        <div key={b.from} className="absolute inset-y-0 bg-background" style={{ left: x(b.from), width: x(b.to) - x(b.from) }} />
                                    ))}
                                    {hours.map((h) => (
                                        <div key={h} className="pointer-events-none absolute inset-y-0 border-l border-border/60" style={{ left: h * CELL * 2 }} />
                                    ))}
                                    {busy.map((b) => <BusyBar key={`${b.source}-${b.start.getTime()}-${b.end.getTime()}`} block={b} left={x(b.start.getTime())} width={Math.max(3, x(b.end.getTime()) - x(b.start.getTime()))} timeZone={timeZone} />)}
                                    {!known && availability.data ? (
                                        <span className="absolute inset-0 flex items-center px-2 text-[10px] text-muted-foreground">
                                            Outside your organisation — no availability shown
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}

                    {/* The proposed meeting, across every row. */}
                    <div
                        aria-hidden
                        className={cn(
                            "pointer-events-none absolute bottom-0 top-6 z-10 rounded-sm border-2",
                            conflictNames.length > 0 ? "border-destructive bg-destructive/10" : "border-primary bg-primary/10"
                        )}
                        style={{ left: NAME_W + x(selStart), width: Math.max(4, x(selEnd) - x(selStart)) }}
                    />
                    {availability.isPending && emails.length > 0 ? (
                        <div className="absolute inset-0 top-6 z-20 flex items-center justify-center bg-card/50">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                <Legend className="bg-sky-600" label="Busy" />
                <Legend style={TENTATIVE} label="Tentative" />
                <Legend className="border bg-muted/70" label="Outside working hours" />
                <span className={cn("ml-auto font-medium", conflictNames.length > 0 ? "text-destructive" : "text-emerald-700 dark:text-emerald-400")} role="status">
                    {availability.isError
                        ? "Availability couldn't be loaded"
                        : !availability.data
                            ? ""
                            : conflictNames.length > 0
                                ? `Conflict: ${conflictNames.join(", ")}`
                                : "Everyone is free"}
                </span>
            </div>

            {suggestions.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <Sparkles className="h-3 w-3" aria-hidden /> Suggested times
                    </span>
                    {suggestions.map((s) => {
                        const active = s.at === selStart;
                        return (
                            <button
                                key={s.at}
                                type="button"
                                onClick={() => onPick(new Date(s.at))}
                                aria-pressed={active}
                                className={cn(
                                    "rounded-full border px-2.5 py-0.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                                )}
                            >
                                {formatTime(new Date(s.at), timeZone)} – {formatTime(new Date(s.at + durationMin * 60_000), timeZone)}
                                {s.conflicts > 0 ? <span className="opacity-75"> · {s.conflicts} conflict{s.conflicts === 1 ? "" : "s"}</span> : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </section>
    );
}

const TENTATIVE: CSSProperties = {
    backgroundImage: "repeating-linear-gradient(135deg, rgba(2,132,199,0.85) 0 3px, rgba(2,132,199,0.25) 3px 7px)"
};

function BusyBar({ block, left, width, timeZone }: { block: BusyBlock; left: number; width: number; timeZone: string }) {
    const what = block.subject ?? (block.showAs === "tentative" ? "Tentative" : "Busy");
    const title = block.allDay
        ? `${what} · all day`
        : `${what} · ${formatTime(block.start, timeZone)} – ${formatTime(block.end, timeZone)}`;
    return (
        <div
            title={title}
            className={cn(
                "absolute overflow-hidden rounded-sm px-1 text-[10px] leading-4 text-white",
                block.allDay ? "top-0 h-1.5 bg-sky-600/60" : "top-1.5 bottom-1.5",
                !block.allDay && block.showAs === "busy" && "bg-sky-600"
            )}
            style={{ left, width, ...(block.showAs === "tentative" && !block.allDay ? TENTATIVE : null) }}
        >
            {!block.allDay && width > 48 && block.subject ? <span className="block truncate">{block.subject}</span> : null}
        </div>
    );
}

function Legend({ className, style, label }: { className?: string; style?: CSSProperties; label: string }) {
    return (
        <span className="flex items-center gap-1">
            <span className={cn("inline-block h-2.5 w-4 rounded-sm", className)} style={style} aria-hidden />
            {label}
        </span>
    );
}

function label(p: AssistantPerson, a: Availability | undefined): string {
    return p.displayName ?? a?.displayName ?? p.email;
}
